import { ApiError, SESSION_KEY } from "@eldrun/api-client";
import type { Account } from "../../net/account.ts";
import { icon } from "../kit/components.ts";
import { h, mount } from "../kit/dom.ts";

/** Link para o site, numa aba nova (o jogo fica aberto). */
const siteLink = (href: string, text: string) => h("a", { href, target: "_blank", rel: "noopener" }, text);

/**
 * Conta no menu (F34; no kit da F80, ui-a "PLAYER NAME"): o retrato, o nome (ou "Convidado") com a
 * linha da conta; embaixo, "Entrar na conta" e o link para criar no site, o
 * formulário de login ou, conectada, o perfil e "Sair". `onChange` avisa o menu (o apelido da sala passa
 * a ser o da conta, e o tutorial pode ter vindo da conta). O feedback foi para o rodapé do menu.
 */
export function accountBox(account: Account, siteUrl: string, onChange: () => void): HTMLElement {
  const box = h("div", { class: "conta", id: "conta", "aria-live": "polite" });
  /** Cabeça do bloco: retrato, nome em destaque e a linha da conta (as Opções ficam só na lista do menu, F113). */
  const head = (name: (Node | string)[], info: string) =>
    h(
      "div",
      { class: "conta-cabeca" },
      h("span", { class: "conta-retrato", "aria-hidden": "true" }, icon("icone-conta")),
      h("div", { class: "conta-texto" }, h("p", { class: "conta-nome" }, ...name), h("p", { class: "conta-info" }, info)),
    );

  let notice = "";
  const showGuest = () =>
    mount(
      box,
      notice ? h("p", { class: "erro", role: "alert" }, notice) : null,
      head(["Convidado"], "Jogando como convidado: a partida não conta para rankings nem conquistas."),
      h("div", { class: "conta-acoes" }, h("button", { type: "button", id: "btn-entrar", class: "k-botao k-botao--secundario", onclick: showForm }, h("span", { class: "k-rotulo" }, "Entrar na conta")), siteLink(`${siteUrl}/cadastro`, "Criar conta no site")),
    );

  const showSigned = (apelido: string) =>
    mount(
      box,
      head(["Conta: ", h("strong", {}, apelido)], "Suas partidas contam para os rankings e as conquistas."),
      h(
        "div",
        { class: "conta-acoes" },
        siteLink(`${siteUrl}/perfil`, "Ver meu perfil"),
        h(
          "button",
          {
            type: "button",
            id: "btn-sair",
            class: "k-botao k-botao--secundario",
            onclick: () => {
              account.logout();
              render();
              onChange();
            },
          },
          h("span", { class: "k-rotulo" }, "Sair"),
        ),
      ),
    );

  function showForm(): void {
    const login = h("input", { id: "login", class: "k-entrada", autocomplete: "username", maxlength: 254, spellcheck: "false", autocapitalize: "off" });
    const senha = h("input", { id: "senha", class: "k-entrada", type: "password", autocomplete: "current-password", maxlength: 256 });
    const error = h("p", { class: "erro", id: "login-erro", role: "alert" });
    const submit = h("button", { type: "submit", class: "k-botao k-botao--primario" }, "Entrar");
    const form = h(
      "form",
      { id: "form-login", "aria-label": "Entrar na conta" },
      h("div", { class: "k-campo" }, h("label", { for: "login", class: "k-campo-nome" }, "Apelido ou e-mail"), login),
      h("div", { class: "k-campo" }, h("label", { for: "senha", class: "k-campo-nome" }, "Senha"), senha),
      error,
      h(
        "div",
        { class: "conta-acoes" },
        submit,
        h(
          "button",
          {
            type: "button",
            class: "k-botao k-botao--secundario",
            onclick: () => {
              account.cancel();
              render();
            },
          },
          "Cancelar",
        ),
        siteLink(`${siteUrl}/cadastro`, "Criar conta no site"),
        // A senha nova se pede no site (F121): o link do e-mail abre lá.
        siteLink(`${siteUrl}/esqueci-senha`, "Esqueci minha senha"),
      ),
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (submit.disabled) return;
      if (!login.value.trim() || !senha.value) {
        error.textContent = "Informe apelido (ou e-mail) e senha.";
        return;
      }
      submit.disabled = true;
      submit.textContent = "Entrando…";
      error.textContent = "";
      account.login(login.value, senha.value).then(
        (session) => {
          // Cancelado no meio (ou outra aba saiu): o formulário já foi fechado.
          if (!session) return;
          render();
          onChange();
        },
        (err: unknown) => {
          error.textContent = err instanceof ApiError ? err.message : "Não foi possível entrar.";
          submit.disabled = false;
          submit.textContent = "Entrar";
          senha.select();
        },
      );
    });
    mount(box, form);
    login.focus();
  }

  function render(): void {
    const s = account.session();
    if (!s) return showGuest();
    notice = "";
    showSigned(s.apelido);
  }

  render();
  // Entrou ou saiu em outra aba (o `localStorage` é o mesmo): o bloco e o apelido acompanham.
  const onStorage = (e: StorageEvent) => {
    if (!box.isConnected) return window.removeEventListener("storage", onStorage);
    if (e.key !== null && e.key !== SESSION_KEY) return;
    if (!box.querySelector("#form-login")) render();
    onChange();
  };
  window.addEventListener("storage", onStorage);
  // Sessão guardada: confere na API (recusada, volta a convidado com o aviso) e traz o tutorial da conta.
  if (account.session()) {
    void account.check().then(() => {
      if (!account.session()) notice = "Sua sessão venceu ou não vale mais. Entre de novo na conta.";
      if (!box.querySelector("#form-login")) render();
      onChange();
    });
  }
  return box;
}
